import { Either, Left, Right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import { Response } from 'superagent'

import { log } from '@/log'
import { DiscountAmount } from '@/models/discount-amount/discount-amount'
import { EventBusForThirdParty } from '@/usecases/event-bus-for-third-party/event-bus'
import { ScriptLoader } from '@/util/scriptLoader'
import store from '@spa/store'
import { CartItem } from '@spa/store/modules/cart/types'
import {
  getFullAddress as getContactFullAddress,
  getFullName as getContactFullName,
} from '@spa/store/modules/contact/functions'
import { Contact } from '@spa/store/modules/contact/types'
import { Product } from '@spa/store/modules/orderHistory/types'
import {
  getFullAddress as getUserFullAddress,
  getFullName as getUserFullName,
  getPhoneticFullName as getUserPhoneticFullName
} from '@spa/store/modules/user/functions'
import { User } from '@spa/store/modules/user/types'

import {
  Atone as AtoneApi,
  AtonePayment,
  AtonePaymentCustomer,
  AtonePaymentDelivery,
  AtonePaymentItem,
} from './api/atone'

const pubkey = process.env.PAYMENT_ATONE_PUBLIC_KEY

const REGEX_ITEM_NAME_REPLACEMENT = /\t/g

type AtonePaymentObjectType =
  AtonePaymentCustomer |
  AtonePaymentDelivery |
  AtonePaymentItem

function normalizeString(str: string): string {
  return str.normalize()
}

function normalizeStringFields<T extends AtonePaymentObjectType>(obj: T): T {

  return Object.keys(obj).reduce<Partial<T>>(
    (prev: Partial<T>, key: string) => {
      const value = obj[key]
      if (typeof value === 'string') {
        return {
          ...prev,
          [key]: normalizeString(value),
        }
      }

      return { ...prev, [key]: value }
    },
    {}
  ) as T
}

function normalizeStrings(info: AtonePayment): AtonePayment {
  info.customer = normalizeStringFields(info.customer)
  info.dest_customers = info.dest_customers.map(normalizeStringFields)
  info.items = info.items.map(normalizeStringFields)

  return info
}

export async function createChecksum(info: AtonePayment): Promise<Either<Error, Response>> {
  return AtoneApi.createChecksum(info)
}

export async function makeAtonePayment(
  salesOrderId: string,
  user: User,
  contact: Contact,
  items: AtonePaymentItem[]
): Promise<Either<Error, AtonePaymentWithChecksum>> {
  const info = makeAtonePaymentWithoutChecksum(
    salesOrderId,
    user,
    contact,
    items
  )

  const normalizedInfo = normalizeStrings(info)

  const checksumResult = await createChecksum(normalizedInfo)
  if (checksumResult.isLeft()) {
    return new Left(checksumResult.value)
  }

  const checksum: string = _.get(checksumResult.value.body, 'data.checksum')

  return new Right({
    ...normalizedInfo,
    checksum,
  })
}

export function makeAtonePaymentWithoutChecksum(
  salesOrderId: string,
  user: User,
  contact: Contact,
  items: AtonePaymentItem[]
): AtonePayment {
  const customer: AtonePaymentCustomer = {
    customer_name: getUserFullName(user),
    customer_family_name: user.last_name!,
    customer_given_name: user.first_name!,
    customer_name_kana: getUserPhoneticFullName(user),
    customer_family_name_kana: user.phonetic_last_name!,
    customer_given_name_kana: user.phonetic_first_name!,
    birthday: user.birthday!,
    sex_division: user.gender,
    zip_code: user.zipcode!,
    address: getUserFullAddress(user),
    tel: user.tel!,
    email: user.email!,
  }

  const destCustomer: AtonePaymentDelivery = {
    dest_customer_name: getContactFullName(contact),
    dest_zip_code: contact.postal_code!,
    dest_address: getContactFullAddress(contact),
    dest_tel: contact.tel!,
  }

  return {
    amount: calculateAmount(items),
    shop_transaction_no: salesOrderId,
    user_no: user.id,
    sales_settled: false,   // 売上確定フラグ：固定でfalse
    customer,
    dest_customers: [
      destCustomer,
    ],
    items,
  }
}

function makeDeliveryItem(shipping: number): AtonePaymentItem {
  return {
    /**
     * @see https://my-color.esa.io/posts/598
     */
    shop_item_id: 'delivery',
    item_name: '送料',
    item_price: shipping,
    item_count: 1,
    item_url: `${location.protocol}${location.host}`, // URL is dummy; always required.
  }
}

function makeDiscountItem(discountPrice: number): AtonePaymentItem {
  return {
    /**
     * @see https://my-color.esa.io/posts/598
     */
    shop_item_id: 'discount',
    item_name: '割引',
    item_price: -discountPrice,
    item_count: 1,
    item_url: `${location.protocol}${location.host}`, // URL is dummy; always required.
  }
}

/**
 * Discount price is applied only to an ordinary purchase, not to reorder.
 *
 * TODO: サービス手数料の対応
 * @see https://github.com/Everforth/growthpalette-front/issues/6264
 */
export function convertCartItems(params: {
  items: CartItem[]
  shipping: number
  discountAmount: DiscountAmount
}): AtonePaymentItem[] {
  return [
    ...params.items.map(convertCartItem),
    ...params.shipping ? [makeDeliveryItem(params.shipping)] : [],
    ...params.discountAmount.totalDiscountAmount ? [makeDiscountItem(params.discountAmount.totalDiscountAmount)] : [],
  ]
}

export function convertCartItem(item: CartItem): AtonePaymentItem {
  return {
    shop_item_id: item.skuId,
    item_name: item.name.replace(REGEX_ITEM_NAME_REPLACEMENT, ''),
    item_price: item.unitPrice,
    item_count: item.unitCount,
    item_url: `${location.protocol}${location.host}/item/${item.rootShortId}`,
  }
}

/**
 * TODO: サービス手数料の対応
 * @see https://github.com/Everforth/growthpalette-front/issues/6264
 */
export function convertOrderHistoryProducts(
  items: Product[],
  shipping: number,
  discountAmount: DiscountAmount
): AtonePaymentItem[] {
  return [
    ...items.map(convertOrderHistoryProduct),
    ...shipping ? [makeDeliveryItem(shipping)] : [],
    ...discountAmount.totalDiscountAmount ? [makeDiscountItem(discountAmount.totalDiscountAmount)] : [],
  ]
}

export function convertOrderHistoryProduct(item: Product): AtonePaymentItem {
  return {
    shop_item_id: item.skuId,
    item_name: item.itemName!,
    item_price: item.unitPrice,
    item_count: item.unitCount,
    item_url: `${location.protocol}${location.host}/item/${item.rootShortId}`,
  }
}

export async function loadAtoneScript() {
  const src = process.env.PAYMENT_ATONE_SCRIPT || ''

  const scriptLoader = ScriptLoader.getInstanceFor<Atone>({
    src,
    attributes: {
      'data-np-global-exports': 'Atone',
    },
    global: 'Atone',
  })

  await scriptLoader.load()
}

/**
 * @see https://my-color.esa.io/posts/597 ドキュメントへアクセスするときに必要なパスへの参照記載
 * @see https://manual.atone.be/jp/5-2-6 atoneドキュメント(basic認証あり)
 */
export function configAtone(
  userId: string,
  options: {
    preToken: string,
    payment: AtonePaymentWithChecksum,
    authenticated: (authenticationToken: string, userNo: string) => Promise<void>,
    cancelled: () => Promise<void>,
    failed: (response: any) => Promise<void>,
    succeeded: (response: any) => Promise<void>,
    error: (name: string, message: string, errors: any[]) => Promise<void>
  }
) {
  const {
    preToken,
    payment,
    authenticated,
    cancelled,
    failed,
    succeeded,
    error,
  } = options

  publishLogEvent('info', userId, 'config', {
    payment,
  })

  Atone.config({
    pre_token: preToken, // 認証token（Engineから取得した認証token）
    pub_key: pubkey, // 公開鍵
    payment,
    // 認証が完了したタイミング、または会員登録が完了したタイミングで呼び出し
    authenticated,
    // モーダルを閉じたタイミングで呼び出し
    cancelled,
    // 決済がNGとなった後、ボタンを押してフォームを閉じたタイミングで呼び出し
    failed,
    // 決済がOKとなり自動でフォームが閉じたタイミングで呼び出し
    succeeded,
    // エラーが発生したタイミングで呼び出し
    error,
  })
}

/**
 * @see https://manual.atone.be/jp/5-2-6
 * 会員が決済モーダルで会員登録またはログイン処理を行い、認証が成功した際に呼び出されるコールバックです。
 * 対象会員の決済認証トークンを返却します。
 * 適宜決済認証トークンを保存する処理を実装してください。
 */
export async function atoneAuthenticatedCallbackFactory(
  userId: string,
  response: {
    authenticationToken: string,
    userNo: string,
  },
  onSavePaymentAuthTokenSucceeded: (authenticationToken: string) => Promise<void>,
  onSavePaymentAuthTokenFailed: () => Promise<void>
): Promise<void> {
  log.debug({
    atone_callback: {
      event: 'authenticated',
      response,
    },
  })
  publishLogEvent('info', userId, 'authenticated', { response })

  await store.dispatch('user/updatePaymentAuthToken', {
    payment_auth_token: response.authenticationToken,
  })
  /**
   * TODO:
   * POST commerce_purchase時にEngineがauth_tokenをuserに転写するので、
   * ここでエラーがあってもユーザーにフィードバックは不要と思われる。
   * したがってonSavePaymentAuthTokenFailed自体が不要になる想定。
   * @see https://github.com/Everforth/growthpalette-front/issues/8531
   */
  const errorOnUserUpdate = _.get(store.state.user, 'error.update')
  if (errorOnUserUpdate) {
    await onSavePaymentAuthTokenFailed()

    // Continue Atone process, because it's just the matter of authentication reusability.
    // And it's not immediately clear how to close Atone modal with a method.
    return
  }

  await onSavePaymentAuthTokenSucceeded(response.authenticationToken)
}

/**
 * @see https://manual.atone.be/jp/5-2-6
 * atoneの決済モーダルを閉じた時に呼び出されるコールバックです。
 * 決済選択画面など、適切な画面に遷移するよう実装してください。
 */
export async function atoneCancelledCallbackFactory(userId: string): Promise<void> {
  log.debug({
    atone_callback: {
      event: 'cancelled',
    },
  })

  publishLogEvent('info', userId, 'cancelled', {})
}

/**
 * @see https://manual.atone.be/jp/5-2-6
 * 与信結果がNGとなった場合に表示される与信NG画面を閉じた時に呼び出されるコールバックです。
 * また、システムエラーが発生した際に表示されるエラー画面で「ほかの支払方法を選ぶ」ボタンを押して画面を閉じた場合にも、決済失敗時コールバックが呼ばれます。
 * その場合、コールバックメソッドに引数は渡されません。
 * 決済選択画面など、適切な画面に遷移するよう実装してください。
 */
export async function atoneFailedCallbackFactory(
  userId: string,
  response: any,
  actionOnError: () => Promise<void>
): Promise<void> {
  log.debug({
    atone_callback: {
      event: 'failed',
      response,
    },
  })

  publishLogEvent('info', userId, 'failed', { response })

  await actionOnError()
}

/**
 * @see https://manual.atone.be/jp/5-2-6
 * 決済が正常に完了し、atoneのモーダル画面が自動的に閉じた後に呼び出されるコールバックです。
 * 取引情報を保存する、購入完了ページに遷移するなど適切な後続処理を実装してください。
 */
export async function atoneSucceededCallbackFactory(
  userId: string,
  response: any,
  actionOnSuccess: () => Promise<void>
): Promise<void> {
  log.debug({
    atone_callback: {
      event: 'succeeded',
      response,
    },
  })

  publishLogEvent('info', userId, 'succeeded', { response })

  await actionOnSuccess()
}

/**
 * @see https://manual.atone.be/jp/5-2-6
 * 設定されたパラメーターに不備がある場合や、atone側の処理で予期しないエラーが発生した場合に呼び出されるコールバックです。
 * エラーの内容をログとして残すよう実装してください。
 */
export async function atoneErrorCallbackFactory(
  userId: string,
  response: {
    name: string,
    message: string,
    errors: any[],
  },
  actionOnError: () => Promise<void>
): Promise<void> {
  log.error({
    atone_callback: {
      event: 'error',
      response,
    },
  })

  publishLogEvent('error', userId, 'error', { response })

  await actionOnError()
}

export interface AtonePaymentWithChecksum extends AtonePayment {
  checksum: string
}

function calculateAmount(items: AtonePaymentItem[]) {
  return _.sum(
    _.map(items, item => item.item_price * item.item_count)
  )
}

function publishLogEvent(
  level: 'error' | 'info',
  userId: string,
  event: string,
  payload: any
) {
  EventBusForThirdParty.getInstance().publish('publish-developer-reports', [{
    userId,
    level,
    timestamp: new Date(),
    message: {
      userId,
      event,
      payload,
    },
    reportSource: 'atone',
  }])
}
